import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getCatalog, getCategories, getGarmentIndex } from '../lib/api';
import type { PageSize } from '../lib/page-size';
import type { CatalogPage, GarmentIndexEntry, GarmentSummary } from '../lib/types';

/**
 * Offset for the next page: the number of garments already loaded.
 *
 * Extracted and exported because it is the one arithmetic in the customer flow
 * that is silently wrong if you get it right-looking — using `pages.length *
 * PAGE_SIZE` breaks the moment a page comes back short.
 */
export function nextOffset(pages: CatalogPage<GarmentSummary>[]): number {
  return pages.reduce((total, page) => total + page.garments.length, 0);
}

/**
 * Name matching for the search box — **case-insensitive substring, anywhere in
 * the name**, so `ana` finds "Anarkali".
 *
 * ⚠️ **Substring, not prefix, and it cuts both ways**: `ana` also matches
 * "B*ana*rasi Silk". That is deliberate — a shopper who half-remembers the middle
 * of a name is the case search exists for, and a prefix match (`name.startsWith`)
 * would silently fail them. Change this and the empty-state copy stops being true.
 *
 * An all-whitespace query returns **nothing**, not everything: `CatalogBrowser`
 * reads an empty result set together with a blank box to decide whether to show
 * the browse grid, so "no text" and "no matches" have to stay distinguishable
 * there rather than here.
 */
/**
 * How many matches either search screen will *draw*. Matching itself is not the
 * cost — filtering 1000 names takes ~0.05ms — but rendering is: at 1000 garments
 * the single letter `a` matches 947 of them, and a list that paints ~950 rows
 * janks a mid-range tablet on every keystroke.
 *
 * ⚠️ **A cap, not paging, and deliberately so.** A second page of search results
 * is a worse answer than a narrower query: nobody scrolls to result 300 looking
 * for a garment they can name. Both screens show the total alongside, so a
 * capped list says "keep typing" rather than quietly lying about how many there
 * are. Virtualising instead would render all of them smoothly and still be the
 * wrong interaction — plus a dependency this app does not have.
 */
export const SEARCH_RESULT_LIMIT = 50;

export function matchGarments(index: GarmentIndexEntry[], search: string): GarmentIndexEntry[] {
  const needle = search.trim().toLowerCase();
  if (needle === '') return [];

  return index.filter((entry) => entry.name.toLowerCase().includes(needle));
}

/**
 * The customer grid.
 *
 * Changing the category filter must reset `offset` to 0 — the server does not
 * do it for you. That falls out of `categories` being part of the query key:
 * a different filter is a different cached query, which starts at
 * `initialPageParam` again. Sorting the array keeps ["a","b"] and ["b","a"]
 * from being two separate caches of the same thing.
 *
 * ⚠️ `pageSize` is in the query key for the same reason. Without it, changing
 * the size would keep serving the pages already cached at the old size and only
 * apply to pages fetched afterwards — a grid half in 20s and half in 50s, with
 * offsets that still happen to line up, so nothing looks broken.
 */
export function useCatalog(categories: string[], pageSize: PageSize) {
  const key = [...categories].sort();

  return useInfiniteQuery({
    queryKey: ['catalog', key, pageSize],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => getCatalog({ limit: pageSize, offset: pageParam, categories: key }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? nextOffset(allPages) : undefined,
  });
}

/**
 * Filter chips come from the *in-stock* category list, not the store's allowed
 * list — a category with no garments produces no chip, with no flag needed.
 */
export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
    staleTime: 5 * 60_000,
  });
}

/**
 * The search index — every garment name in the store, fetched once.
 *
 * `staleTime` matches `useCategories` for the same reason: the catalogue is
 * edited off-hours by one operator, never while a customer browses, so a name
 * added mid-session being invisible for five minutes is not a case that happens.
 *
 * ⚠️ **Not keyed on the search text.** The query the customer types never reaches
 * the server — filtering happens in `CatalogBrowser` against this one cached
 * list, which is what makes typing cost nothing on shop wifi. Putting the text
 * in the key here would turn every keystroke back into a request and defeat the
 * whole design.
 */
export function useGarmentIndex() {
  return useQuery({
    queryKey: ['garment-index'],
    queryFn: getGarmentIndex,
    staleTime: 5 * 60_000,
  });
}
